// Fotos zeigen (E2, Schritt 2; Mockup: fotos.js, ansichten-doku.js): das
// Foto mit der Zeichnung als Ebene darüber – dieselbe Darstellung wie im
// Büro, weil sie aus dem Kern kommt (zeichnungSvgInhalt) –, kleine Marken
// „gezeichnet“ und „Notiz“, das Raster an der Station mit der Kachel
// „Foto“ und der Streifen unter dem großen Foto. Seit Schritt 4 auch nur zum
// Lesen (ohne Kachel „Foto“), und ein Foto, das nicht mehr am Gerät liegt,
// kommt aus dem Büro. Seit Schritt 5 mit einer kleinen Marke oben links:
// „neu“ im laufenden Nachtrag, „nachgereicht“ beim Nachsehen.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from "react";
import { neueKennung, zeichnungSvgInhalt, type Zeichnung } from "@happy-energy/kern";
import { fehlerMelden, Ikon, ohneNetz, toastZeigen, type IkonName } from "@happy-energy/oberflaeche";
import type { Foto, Station } from "../speicher.ts";
import { aufnahmezeitLesen, fotoVorbereiten } from "../foto.ts";
import { fotoHinzufuegen, fotoLoeschen, SchonGesendet, type Bearbeiten } from "../arbeitskopie.ts";
import { dateiZumAnsehen, holenFehlgeschlagen, type DateiStand } from "../dateien.ts";
import { useMemoAufnahme } from "../memo.ts";
import {
  fortschrittText,
  fotosUebernehmen,
  laufendeUebernahmen,
  nachAufnahmezeit,
  sehrGross,
  uebernahmeMelden,
  uebernahmeMeldung,
  uebernahmenAbonnieren,
  uebernahmeStand,
  type Fehlerart,
  type LaufendeUebernahme,
} from "../vom-geraet.ts";
import { keinPlatz, speichernFehlgeschlagen } from "./gemeinsam.tsx";
import { FrageMarke } from "./fragen.tsx";

export type { DateiStand } from "../dateien.ts";

/**
 * Was gerade an Fotos übernommen wird – an jeder Station, auch nach dem
 * Verlassen (seit E3c, S5; O94): für den Fortschritt, die Sperre von „Vom
 * Gerät“ und damit „Senden“ wartet (vom-geraet.ts).
 */
export function useUebernahmen(): readonly LaufendeUebernahme[] {
  return useSyncExternalStore(uebernahmenAbonnieren, laufendeUebernahmen, laufendeUebernahmen);
}

// --- Die Adresse eines Fotos zum Anzeigen ----------------------------------------------
//
// Ein Foto liegt als ArrayBuffer in „dateien“ (dateien.ts); zum Anzeigen
// braucht es einen Blob im Arbeitsspeicher und dessen Adresse (blob:…). Sie
// hängt an der Prüfsumme: gleicher Inhalt, dieselbe Adresse – das Bild
// flackert nicht bei jedem Tippen, und kein Neu-Speichern des Datensatzes
// macht sie ungültig (seit 19.10.2026; vorher zeigte sie auf den Blob aus
// IndexedDB, den Safari nach dem Senden unlesbar machte – Fragezeichen statt
// Foto). Wird ein Foto nirgends mehr gezeigt, gibt die App die Adresse nach
// einer Weile frei.
//
// Seit Schritt 4: Liegt das Foto nicht (mehr) am Gerät – nach dem Senden
// sind die Dateien weg –, holt `dateiZumAnsehen` es aus dem Büro. Ohne
// Empfang steht da, dass es sicher im Büro liegt; kommt das Netz zurück,
// versucht die App es von selbst noch einmal.

const LAEDT: DateiStand = { art: "laedt" };

interface Adresse {
  stand: DateiStand;
  nutzer: number;
  zuhoerer: Set<() => void>;
  uhr?: ReturnType<typeof setTimeout>;
}

const adressen = new Map<string, Adresse>();

function standSetzen(pruefsumme: string, a: Adresse, stand: DateiStand): void {
  // Inzwischen freigegeben: Die Adresse braucht niemand mehr.
  if (adressen.get(pruefsumme) !== a) {
    if (stand.art === "da") URL.revokeObjectURL(stand.url);
    return;
  }
  a.stand = stand;
  for (const z of a.zuhoerer) z();
}

/** Liest das Foto – am Gerät, sonst aus dem Büro – und macht daraus die Adresse. */
function laden(pruefsumme: string, a: Adresse): void {
  if (a.stand.art !== "laedt") standSetzen(pruefsumme, a, LAEDT);
  dateiZumAnsehen(pruefsumme, undefined, () => standSetzen(pruefsumme, a, { art: "holt" })).then(
    (blob) => standSetzen(pruefsumme, a, blob ? { art: "da", url: URL.createObjectURL(blob) } : { art: "fehlt" }),
    (fehler: unknown) => {
      const stand = holenFehlgeschlagen(fehler);
      if (stand.art === "fehlt") fehlerMelden("doku", fehler);
      standSetzen(pruefsumme, a, stand);
    },
  );
}

/** Kommt das Netz zurück, holt die App, was vorher ohne Empfang nicht ging – einmal angemeldet, für alle Fotos. */
let netzHoert = false;
function beiNetzNochmal(): void {
  if (netzHoert) return;
  netzHoert = true;
  window.addEventListener("online", () => {
    for (const [pruefsumme, a] of adressen) if (a.stand.art === "ohne-netz" && a.nutzer > 0) laden(pruefsumme, a);
  });
}

function adresseNutzen(pruefsumme: string, melden: () => void): () => void {
  beiNetzNochmal();
  let a = adressen.get(pruefsumme);
  if (!a) {
    a = { stand: LAEDT, nutzer: 0, zuhoerer: new Set() };
    adressen.set(pruefsumme, a);
    laden(pruefsumme, a);
  } else if (a.stand.art === "ohne-netz" && !ohneNetz()) {
    // Wieder gezeigt, und das Gerät meldet Netz: noch einmal versuchen.
    laden(pruefsumme, a);
  }
  if (a.uhr) clearTimeout(a.uhr);
  a.uhr = undefined;
  a.nutzer += 1;
  a.zuhoerer.add(melden);
  const eintrag = a;
  return () => {
    eintrag.zuhoerer.delete(melden);
    eintrag.nutzer -= 1;
    if (eintrag.nutzer > 0) return;
    eintrag.uhr = setTimeout(() => {
      if (eintrag.stand.art === "da") URL.revokeObjectURL(eintrag.stand.url);
      adressen.delete(pruefsumme);
    }, 20_000);
  };
}

/** Wie es um die Datei mit dieser Prüfsumme steht: lädt, kommt aus dem Büro, da (mit Adresse), ohne Empfang oder fehlt. */
export function useDateiStand(pruefsumme: string): DateiStand {
  const abonnieren = useCallback((melden: () => void) => adresseNutzen(pruefsumme, melden), [pruefsumme]);
  return useSyncExternalStore(
    abonnieren,
    () => adressen.get(pruefsumme)?.stand ?? LAEDT,
    () => LAEDT,
  );
}

/** Die Adresse (blob:…) eines Fotos zum Anzeigen – null, solange sie lädt oder die Datei fehlt. */
export function useDateiAdresse(foto: { pruefsumme: string }): string | null {
  const stand = useDateiStand(foto.pruefsumme);
  return stand.art === "da" ? stand.url : null;
}

// --- Foto mit Zeichnung -------------------------------------------------------------------

/**
 * Die Marke eines Stücks nach seiner Sendung (seit Schritt 5): im laufenden
 * Nachtrag „neu“ an dem, was mit ihm dazukam (das lässt sich bearbeiten);
 * beim Nachsehen „nachgereicht“ an allem, was mit einem Nachtrag kam. Beim
 * Aufnehmen keine.
 */
export function sendungsMarke(x: { nachtragId: string | null }, modus: Bearbeiten): string | null {
  if (modus.art === "nachtrag") return x.nachtragId === modus.nachtragId ? "neu" : null;
  if (modus.art === "gesperrt") return x.nachtragId ? "nachgereicht" : null;
  return null;
}

/** Ob auf dem Foto gezeichnet ist. */
export function gezeichnet(foto: Pick<Foto, "zeichnung">): boolean {
  return (foto.zeichnung?.striche.length ?? 0) > 0;
}

/**
 * Die Zeichnung als Ebene über dem Foto. Das SVG entsteht im Kern aus
 * Zahlen – kein Text vom Gerät kommt hinein (zeichnung.ts). „decken“ legt
 * es wie ein Foto mit object-fit: cover (Kachel), sonst passt es genau.
 */
export function ZeichenEbene({
  zeichnung,
  breite,
  hoehe,
  decken = false,
}: {
  zeichnung: Zeichnung | null;
  breite: number;
  hoehe: number;
  decken?: boolean;
}) {
  if (!zeichnung || zeichnung.striche.length === 0) return null;
  return (
    <svg
      className="zeichen-ebene"
      viewBox={`0 0 ${breite} ${hoehe}`}
      preserveAspectRatio={decken ? "xMidYMid slice" : "none"}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: zeichnungSvgInhalt(zeichnung, breite, hoehe) }}
    />
  );
}

/** Das Seitenverhältnis eines Fotos als CSS-Wert – für das ganze Foto ohne Beschnitt. */
export function seitenverhaeltnis(foto: Pick<Foto, "breite" | "hoehe">): CSSProperties {
  return { "--seitenverhaeltnis": foto.breite / foto.hoehe } as CSSProperties;
}

/** Was der Platzhalter sagt und zeigt – je nachdem, wie es um die Datei steht. */
const PLATZHALTER: Record<Exclude<DateiStand["art"], "da">, { text: string; ikon: IkonName | null }> = {
  laedt: { text: "Foto wird geladen …", ikon: null },
  holt: { text: "Foto wird aus dem Büro geholt …", ikon: null },
  "ohne-netz": { text: "Ohne Empfang – das Foto liegt sicher im Büro", ikon: "offline" },
  fehlt: { text: "Foto am Gerät nicht lesbar", ikon: "achtung" },
};

/**
 * Statt des Fotos, solange es lädt, aus dem Büro kommt oder nicht zu
 * bekommen ist – mit Worten, nie ein Fragezeichen. In der Kachel nur das
 * Zeichen (Kreisel, kein Netz, Achtung); die Worte stehen als Beschriftung dran.
 */
function FotoPlatzhalter({ stand, klein }: { stand: Exclude<DateiStand["art"], "da">; klein: boolean }) {
  const { text, ikon } = PLATZHALTER[stand];
  return (
    <span className={`foto-leer${stand === "fehlt" ? " foto-fehlt" : stand === "ohne-netz" ? " foto-ohne-netz" : ""}`} role="img" aria-label={text}>
      {ikon ? <Ikon name={ikon} /> : <span className="kreisel" aria-hidden="true" />}
      {!klein && <span className="foto-leer-text">{text}</span>}
    </span>
  );
}

/**
 * Ein Foto mit seiner Zeichnung. „kachel“: im Raster, beschnitten auf 4:3,
 * mit Marken – unten „gezeichnet“ und „Notiz“, oben links `marke` („neu“,
 * „nachgereicht“), oben rechts seit E3 `frage` (eine offene Frage am Foto
 * oder seiner Notiz). „ganz“: das ganze Foto ohne Beschnitt.
 */
export function FotoBild({
  foto,
  art,
  notiz = false,
  marke = null,
  frage = false,
  alt,
}: {
  foto: Foto;
  art: "kachel" | "ganz";
  notiz?: boolean;
  marke?: string | null;
  frage?: boolean;
  alt: string;
}) {
  const stand = useDateiStand(foto.pruefsumme);
  // Das Bild selbst ließ sich nicht zeigen (kaputte Datei) – dann wie „fehlt“, nie ein Fragezeichen.
  const [kaputt, setKaputt] = useState<string | null>(null);
  const kachel = art === "kachel";
  const url = stand.art === "da" && kaputt !== stand.url ? stand.url : null;
  return (
    <div className={`foto${kachel ? "" : " foto-ganz"}`} style={kachel ? undefined : seitenverhaeltnis(foto)}>
      {url ? (
        <img src={url} alt={alt} draggable={false} onError={() => setKaputt(url)} />
      ) : (
        <FotoPlatzhalter stand={stand.art === "da" ? "fehlt" : stand.art} klein={kachel} />
      )}
      <ZeichenEbene zeichnung={foto.zeichnung} breite={foto.breite} hoehe={foto.hoehe} decken={kachel} />
      {kachel && marke && <span className="sendungs-marke foto-sendung">{marke}</span>}
      {kachel && frage && <FrageMarke titel="offene Frage" />}
      {kachel && (gezeichnet(foto) || notiz) && (
        <span className="foto-marken">
          {gezeichnet(foto) && (
            <span className="mini-marke lime" title="gezeichnet">
              <Ikon name="stift" groesse="k" />
            </span>
          )}
          {notiz && (
            <span className="mini-marke" title="Notiz zum Foto">
              <Ikon name="notiz" groesse="k" />
            </span>
          )}
        </span>
      )}
    </div>
  );
}

// --- Die Fotos einer Station --------------------------------------------------------------

/**
 * Das kleine ✕ an einer Foto-Kachel (Gernots Augentest, 27.10.2026): nur,
 * wo sich das Foto auch auf seiner Seite löschen ließe (`loeschbar` –
 * dieselbe Regel wie dort, `fotoBearbeitbar`), und nicht, solange an genau
 * diesem Foto ein Sprachmemo aufgenommen wird.
 */
export function kreuzZeigen(fotoId: string, loeschbar: boolean, memoAm: string | null): boolean {
  return loeschbar && memoAm !== fotoId;
}

/**
 * Das Foto-Raster einer Station: jedes Foto antippen öffnet es groß; die
 * Kachel „Foto“ öffnet die Kamera. Solange noch keines da ist, ist die
 * Kachel breit und sagt, was sie tut. Während ein Foto verkleinert wird
 * (am iPhone rund eine Sekunde), steht ein Platzhalter da. `nurLesen` (seit
 * Schritt 4, gesendet): ohne Kachel „Foto“ – nur ansehen. Im Nachtrag (seit
 * Schritt 5) ist die Kachel da: Das neue Foto kommt mit ihm dazu. Seit O91
 * daneben „Vom Gerät“: Fotos, die schon am Gerät liegen, mehrere auf einmal
 * (vom-geraet.ts) – dieselben Regeln wie beim Fotografieren. Seit dem
 * Augentest vom 27.10. hat jede löschbare Kachel oben rechts ein kleines ✕
 * mit derselben Rückfrage wie die Foto-Seite – ein versehentlich eingefügtes
 * Foto ist so gleich weg.
 */
export function FotoRaster({
  station,
  fotos,
  mitNotiz,
  mitFrage,
  adresse,
  marke = () => null,
  nurLesen = false,
  loeschbar = () => false,
}: {
  station: Pick<Station, "id" | "aufnahmeId">;
  fotos: Foto[];
  /** Die Fotos mit Notiz (Kennungen). */
  mitNotiz: ReadonlySet<string>;
  /** Die Fotos mit offener Frage (Kennungen, seit E3) – ohne: keine Marke. */
  mitFrage?: ReadonlySet<string>;
  /** Wohin ein Foto führt. */
  adresse: (foto: Foto) => string;
  /** Die Marke oben links (`sendungsMarke`) – ohne: keine. */
  marke?: (foto: Foto) => string | null;
  nurLesen?: boolean;
  /** Lässt sich dieses Foto löschen (`fotoBearbeitbar`)? Ohne: kein ✕. */
  loeschbar?: (foto: Foto) => boolean;
}) {
  const [inArbeit, setInArbeit] = useState(0);
  // Das ✕ (27.10.2026): zu welchem Foto gerade „Dieses Foto löschen?“ gefragt wird.
  const [fragtZu, setFragtZu] = useState<string | null>(null);
  const rueckfrageKasten = useRef<HTMLDivElement>(null);
  // Die Rückfrage ganz zeigen – sonst lägen ihre Knöpfe unter dem festen Fuß.
  useEffect(() => {
    if (fragtZu) rueckfrageKasten.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [fragtZu]);
  const memo = useMemoAufnahme();
  const memoAm = memo.art !== "aus" ? memo.ort : null;
  const gefragtes = fragtZu ? (fotos.find((f) => f.id === fragtZu) ?? null) : null;

  async function loeschen(foto: Foto) {
    setFragtZu(null);
    try {
      await fotoLoeschen(foto);
      toastZeigen("Foto gelöscht");
    } catch (fehler) {
      speichernFehlgeschlagen(fehler, "Konnte das Foto nicht löschen – bitte noch einmal.");
    }
  }
  // Fotos „Vom Gerät“ (O91): wie weit das Übernehmen an DIESER Station ist – null, wenn hier keines läuft. Seit E3c
  // (S5; O94) steht es außerhalb der Station (vom-geraet.ts): Es läuft weiter, wenn man sie verlässt, und solange
  // irgendwo eines läuft, ist „Vom Gerät“ überall gesperrt – nie zwei zugleich.
  const laufende = useUebernahmen();
  const uebernahme = uebernahmeStand(laufende, station.aufnahmeId, station.id);
  const sperre = laufende.some((u) => u.art === "vom-geraet");

  async function vomGeraet(dateien: File[]) {
    if (dateien.length === 0) return;
    let gemeldet = false;
    const lauf = neueKennung();
    const melden = (fertig: number, gesamt: number) => uebernahmeMelden(lauf, { aufnahmeId: station.aufnahmeId, stationId: station.id, art: "vom-geraet", fertig, gesamt });
    // Gleich sperren – auch während die Zeiten gelesen werden, kein zweites Mal „Vom Gerät“.
    melden(0, dateien.length);
    try {
      // O93 (Gernot, 27.10.2026): das älteste zuerst – je Foto nur die Zeit lesen, nichts dekodieren.
      const geordnet = await nachAufnahmezeit(dateien, aufnahmezeitLesen);
      const ergebnis = await fotosUebernehmen(
        geordnet,
        async (roh) => {
          const bereit = await fotoVorbereiten(roh);
          await fotoHinzufuegen(station, bereit);
        },
        async (fehler, roh): Promise<Fehlerart> => {
          if (fehler instanceof SchonGesendet) return { art: "gesendet", meldung: fehler.message };
          if (keinPlatz(fehler)) return { art: "platz" };
          // O94: Dem Gerät ging für ein sehr großes Foto der Speicher aus – das sagen, statt „ließ sich nicht lesen“.
          if (fehler instanceof RangeError || (await sehrGross(roh))) return { art: "zu-gross" };
          // Einmal je Auswahl an die Fehlerwache – ein Video oder ein kaputtes Foto ist kein Fehler der App.
          if (!gemeldet) fehlerMelden("doku", fehler);
          gemeldet = true;
          return { art: "ueberspringen" };
        },
        melden,
      );
      const m = uebernahmeMeldung(ergebnis);
      // O94: Fehlt ein Foto, bleibt die Meldung stehen, bis man sie antippt – sonst geht sie in 2,6 Sekunden unter.
      toastZeigen(m.text, m.art, { bleibt: m.art === "fehler" });
    } finally {
      uebernahmeMelden(lauf, null);
    }
  }

  async function aufgenommen(roh: File) {
    setInArbeit((n) => n + 1);
    // Seit O94: Auch ein Foto aus der Kamera, das gerade verkleinert wird, lässt „Senden“ warten.
    const lauf = neueKennung();
    uebernahmeMelden(lauf, { aufnahmeId: station.aufnahmeId, stationId: station.id, art: "kamera", fertig: 0, gesamt: 1 });
    try {
      const bereit = await fotoVorbereiten(roh);
      await fotoHinzufuegen(station, bereit);
    } catch (fehler) {
      if (fehler instanceof SchonGesendet) {
        toastZeigen(fehler.message, "fehler");
        return;
      }
      fehlerMelden("doku", fehler);
      toastZeigen(
        keinPlatz(fehler) ? "Kein Platz mehr am Gerät – bitte Speicher freigeben." : "Dieses Foto ließ sich nicht verarbeiten.",
        "fehler",
      );
    } finally {
      uebernahmeMelden(lauf, null);
      setInArbeit((n) => n - 1);
    }
  }

  const leer = fotos.length === 0 && inArbeit === 0;
  if (nurLesen && fotos.length === 0) return <div className="leer-karte">Keine Fotos an dieser Station.</div>;
  return (
    <div className="foto-raster">
      {fotos.map((f, i) => (
        <div key={f.id} className={`foto-zelle${fragtZu === f.id ? " gefragt" : ""}`}>
          <a className="foto-link" href={adresse(f)} aria-label={`Foto ${i + 1} öffnen`}>
            <FotoBild foto={f} art="kachel" notiz={mitNotiz.has(f.id)} marke={marke(f)} frage={mitFrage?.has(f.id) ?? false} alt={`Foto ${i + 1}`} />
          </a>
          {!nurLesen && kreuzZeigen(f.id, loeschbar(f), memoAm) && (
            <button type="button" className="foto-kreuz" aria-label={`Foto ${i + 1} löschen`} onClick={() => setFragtZu(f.id)}>
              <span className="foto-kreuz-kreis" aria-hidden="true">
                <Ikon name="kreuz" groesse="k" />
              </span>
            </button>
          )}
        </div>
      ))}
      {gefragtes && (
        <div ref={rueckfrageKasten} className="rueckfrage foto-rueckfrage" role="alertdialog" aria-labelledby="foto-rueckfrage-titel">
          <b id="foto-rueckfrage-titel">Dieses Foto löschen?</b>
          <span>Zeichnung, Notiz und Sprachmemos dazu gehen mit. Das lässt sich nicht rückgängig machen.</span>
          <div className="knopf-reihe">
            <button type="button" className="knopf-rand" onClick={() => setFragtZu(null)}>
              Behalten
            </button>
            <button type="button" className="knopf-loeschen" onClick={() => void loeschen(gefragtes)}>
              <Ikon name="muell" /> Löschen
            </button>
          </div>
        </div>
      )}
      {Array.from({ length: inArbeit }, (_, i) => (
        <div key={`wird-${i}`} className="foto foto-wird" role="status" aria-label="Foto wird vorbereitet">
          <span className="kreisel" />
        </div>
      ))}
      {uebernahme && (
        <div className="foto-uebernahme" role="status">
          <span className="kreisel" aria-hidden="true" />
          {fortschrittText(uebernahme.fertig, uebernahme.gesamt)}
        </div>
      )}
      {!nurLesen && <FotoKachel leer={leer} aufgenommen={(datei) => void aufgenommen(datei)} />}
      {!nurLesen && <VomGeraetKachel leer={leer} gewaehlt={(dateien) => void vomGeraet(dateien)} sperre={sperre} />}
    </div>
  );
}

/**
 * Die Kachel „Foto“: öffnet die Kamera. Solange noch kein Foto da ist, breit
 * und mit Worten – seit Gernots Augentest (27.10.2026) als Paar mit „Vom
 * Gerät“ in einer Reihe (`paar`: zwei Drittel, „Vom Gerät“ ein Drittel).
 */
function FotoKachel({ leer, aufgenommen }: { leer: boolean; aufgenommen: (datei: File) => void }) {
  return (
    <label className={`foto-neu${leer ? " breit paar" : ""}`}>
      <Ikon name="kamera" groesse="g" />
      {leer ? (
        <span className="foto-neu-wort">
          <b>Foto machen</b>
          <span>Danach antippen: darauf zeichnen oder eine Notiz dazu</span>
        </span>
      ) : (
        <span>Foto</span>
      )}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const datei = e.target.files?.[0];
          // Leeren, damit dasselbe Foto noch einmal gewählt werden kann.
          e.target.value = "";
          if (datei) aufgenommen(datei);
        }}
      />
    </label>
  );
}

/**
 * Die Kachel „Vom Gerät“ (O91): Fotos, die schon am Gerät liegen – aus
 * „Fotos“ oder „Dateien“, mehrere auf einmal (ohne `capture` bietet das
 * iPhone die Mediathek und „Dateien“ an). Ruhiger als „Foto machen“: das
 * Fotografieren bleibt der Hauptweg. Seit Gernots Augentest (27.10.2026)
 * ohne erklärenden Satz – der stand sonst in jeder Station, auch beim
 * Wechselrichter –, und solange noch kein Foto da ist, als weiße Kachel
 * neben der schwarzen „Foto machen“ (`neben`): ein Paar, gleich hoch, statt
 * einer gestrichelten Zeile darunter („schaut ziemlich billig aus“).
 */
function VomGeraetKachel({ leer, gewaehlt, sperre }: { leer: boolean; gewaehlt: (dateien: File[]) => void; sperre: boolean }) {
  return (
    <label className={`foto-neu vom-geraet${leer ? " neben" : ""}${sperre ? " gesperrt" : ""}`}>
      <Ikon name="bilder" groesse="g" />
      <span>Vom Gerät</span>
      <input
        type="file"
        // Nur JPEG und PNG (Code-Prüfung O91, Befund 2): Dann wandelt iOS Fotos im
        // Format HEIC aus der Mediathek selbst in JPEG um – samt Exif, also mit der
        // richtigen Aufnahmezeit. Bei „image/*“ käme HEIC durch, und die Zeit wäre
        // die der Datei (docs/2026-10-12-recherchen-plan-modus.md, „HEIC“).
        accept="image/jpeg,image/png"
        multiple
        disabled={sperre}
        onChange={(e) => {
          const dateien = Array.from(e.target.files ?? []);
          // Leeren, damit dieselben Fotos noch einmal gewählt werden können.
          e.target.value = "";
          gewaehlt(dateien);
        }}
      />
    </label>
  );
}
